// NOTE: "Workflow" in UI = "Deal" in backend API
import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// Critical path - loaded eagerly (auth pages users see first)
import AdminLoginPage from './pages/Auth/AdminLoginPage';
import ProtectedRoute from './components/admin/ProtectedRoute';
import { AuthProvider } from './context/AuthContext';
import { OrganizationProvider } from './context/OrganizationContext';
import { AgentProvider } from './context/AgentContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import { ErrorProvider } from './components/common/ErrorHandler';
import { NotificationProvider } from './context/NotificationContext';
import { SystemNotificationsProvider } from './context/SystemNotificationsContext';
import { LoadingProvider } from './context/LoadingContext';
import { ThemeProvider } from './context/ThemeContext';
import { BrandingProvider } from './context/BrandingContext';
import Notification from './components/common/Notification';
import MessagePushListener from './components/realtime/MessagePushListener';
import PwaManager from './components/pwa/PwaManager';
// Les ecrans de workflows utilisent react-hot-toast : sans ce Toaster, leurs messages
// (erreurs de validation comprises) etaient invisibles et l'enregistrement semblait muet.
import { Toaster } from 'react-hot-toast';

// Import du CSS du thème light
import './admin-light-theme.css';

// OPTIMIZED: Lazy-load all non-critical routes to reduce initial bundle by ~60%
const ForgotPasswordPage = lazy(() => import('./pages/Auth/ForgotPasswordPage'));
const SettingsAgents = lazy(() => import('./pages/Settings/SettingsAgents'));
const GlobalStatsPage = lazy(() => import('./pages/Dashboard/GlobalStatsPage'));
const OrganizationStatsPage = lazy(() => import('./pages/Dashboard/OrganizationStatsPage'));
const ServiceClientActivitiesPage = lazy(() => import('./pages/Dashboard/ServiceClientActivitiesPage'));
const ServiceActivitiesPage = lazy(() => import('./pages/Dashboard/ServiceActivitiesPage'));
const AgentDetailPage = lazy(() => import('./pages/Settings/AgentDetailPage'));
const KnowledgeBasePage = lazy(() => import('./pages/Settings/KnowledgeBasePage'));
const WidgetDemoPage = lazy(() => import('./pages/WidgetDemoPage'));
const EnhancedWidgetDemoPage = lazy(() => import('./pages/Settings/EnhancedChatWidget'));
const GenericAgentDemo = lazy(() => import('./pages/GenericAgentDemo'));
const EmbedDemoPage = lazy(() => import('./pages/EmbedDemoPage'));
const WidgetLiveEmbedPage = lazy(() => import('./pages/WidgetLiveEmbedPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const LiveChat = lazy(() => import('./pages/Conversations/LiveChat'));
const TicketActivities = lazy(() => import('./pages/Tickets/TicketActivities'));
const AssignedToMe = lazy(() => import('./pages/Tickets/AssignedToMe'));
const UsersAdmins = lazy(() => import('./pages/Admins/UsersAdmins'));
const UsersAgents = lazy(() => import('./pages/Agents/UsersAgents'));
const UsersClients = lazy(() => import('./pages/Clients/UsersClients'));
const CampaignsRelance = lazy(() => import('./pages/Campaigns/CampaignsRelance'));
const CampaignsAnalytics = lazy(() => import('./pages/Campaigns/CampaignsAnalytics'));
// NOTE: "Workflow" in UI = "Deal" in backend API - components live under Settings/Deal* and Deals/Deal*
const WorkflowTemplates = lazy(() => import('./pages/Settings/DealTemplates'));
const WorkflowTemplateEditor = lazy(() => import('./pages/Settings/DealTemplateEditor'));
const WorkflowList = lazy(() => import('./pages/Deals/DealList'));
const WorkflowValidationPage = lazy(() => import('./pages/Deals/DealValidationPage'));
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage'));
const NotificationsPage = lazy(() => import('./pages/Notifications/NotificationsPage'));
const PreferencesPage = lazy(() => import('./pages/Settings/PreferencesPage'));
const AppearancePage = lazy(() => import('./pages/appearance/AppearancePage'));
const FacebookCallback = lazy(() => import('./pages/Channels/FacebookCallback'));

// Minimal loading fallback - no heavy spinners
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <div style={{ width: 32, height: 32, border: '3px solid #e5e7eb', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
  </div>
);

// Wrapper to reduce repetition
const Protected = ({ children }) => (
  <ProtectedRoute>
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  </ProtectedRoute>
);

function App() {
  return (
    <ErrorBoundary>
      <ErrorProvider>
        <BrandingProvider>
        <ThemeProvider>
          <AuthProvider>
            <OrganizationProvider>
              <AgentProvider>
                <NotificationProvider>
                  <SystemNotificationsProvider>
                  <LoadingProvider>
                    <Notification />
                    <Toaster position="top-right" toastOptions={{ duration: 5000 }} />
                    <PwaManager />
                    <MessagePushListener />
                    <Suspense fallback={<PageLoader />}>
                    <Routes>
                    <Route path="/" element={<Navigate to="/admin/login" replace />} />

                    {/* Auth routes - no protection needed */}
                    <Route path="/admin/login" element={<AdminLoginPage />} />
                    <Route path="/admin/forgot-password" element={<ForgotPasswordPage />} />

                    {/* Dashboard */}
                    {/* TODO MERGE: main added a generic /admin/dashboard -> GlobalStatsPage
                        (pages/Dashboard/GlobalStatsPage.jsx). HEAD instead redirects /admin/dashboard to the
                        CRM-style /admin/service-activities view ("Nouveau Dashboard CRM Style" rework, matches
                        the sidebar nav item). Kept HEAD's redirect behavior here; GlobalStatsPage.jsx is left
                        unrouted/unused on disk - a human should decide whether to expose it on another URL or
                        delete it. */}
                    <Route path="/admin/dashboard" element={<Protected><Navigate to="/admin/service-activities" replace /></Protected>} />
                    <Route path="/admin/stats" element={<Protected><OrganizationStatsPage /></Protected>} />
                    <Route path="/admin/service-activities" element={<Protected><ServiceActivitiesPage /></Protected>} />
                    <Route path="/admin/service-client-activities" element={<Protected><ServiceClientActivitiesPage /></Protected>} />

                    {/* Conversations */}
                    <Route path="/conversations/live/:clientId/:conversationId" element={<Protected><LiveChat /></Protected>} />
                    <Route path="/conversations/live/:clientId" element={<Protected><LiveChat /></Protected>} />
                    <Route path="/conversations/live" element={<Protected><LiveChat /></Protected>} />

                    {/* Tickets */}
                    <Route path="/tickets/activities" element={<Protected><TicketActivities /></Protected>} />
                    <Route path="/tickets/assigned-to-me" element={<Protected><AssignedToMe /></Protected>} />

                    {/* Users */}
                    <Route path="/users/admins" element={<Protected><UsersAdmins /></Protected>} />
                    <Route path="/users/agents" element={<Protected><UsersAgents /></Protected>} />
                    <Route path="/users/clients" element={<Protected><UsersClients /></Protected>} />

                    {/* Campaigns */}
                    <Route path="/campaigns/relance" element={<Protected><CampaignsRelance /></Protected>} />
                    <Route path="/campaigns/analytics" element={<Protected><CampaignsAnalytics /></Protected>} />

                    {/* Settings */}
                    <Route path="/settings/agents" element={<Protected><SettingsAgents /></Protected>} />
                    <Route path="/admin/agents/:agentId" element={<Protected><AgentDetailPage /></Protected>} />
                    <Route path="/admin/agents/:agentId/knowledge" element={<Protected><KnowledgeBasePage /></Protected>} />
                    <Route path="/admin/knowledge" element={<Protected><KnowledgeBasePage /></Protected>} />

                    {/* Workflow Management Routes (UI label "Workflow" = backend "Deal") */}
                    <Route path="/settings/workflows" element={<Protected><WorkflowTemplates /></Protected>} />
                    <Route path="/settings/workflows/new" element={<Protected><WorkflowTemplateEditor /></Protected>} />
                    <Route path="/settings/workflows/edit/:templateId" element={<Protected><WorkflowTemplateEditor /></Protected>} />
                    <Route path="/settings/workflows/list" element={<Protected><WorkflowList /></Protected>} />
                    <Route path="/admin/workflows/validation/:workflowId" element={<Protected><WorkflowValidationPage /></Protected>} />
                    {/* Legacy path still emitted by the client-activities table. Kept as an
                        alias so those links open the deal instead of hitting the 404 page. */}
                    <Route path="/admin/deals/validation/:dealId" element={<Protected><WorkflowValidationPage /></Protected>} />

                    {/* Demos */}
                    <Route path="/demo" element={<Protected><WidgetDemoPage /></Protected>} />
                    <Route path="/restaurant-demo" element={<Protected><EnhancedWidgetDemoPage /></Protected>} />
                    <Route path="/agent-demo" element={<Protected><GenericAgentDemo /></Protected>} />
                    <Route path="/embed-demo/:agentId" element={<Protected><EmbedDemoPage /></Protected>} />
                    <Route path="/widget-live" element={<Protected><WidgetLiveEmbedPage /></Protected>} />

                    {/* Channels */}
                    <Route path="/channels/facebook/callback" element={<Protected><FacebookCallback /></Protected>} />

                    {/* Profile & Settings */}
                    <Route path="/profile" element={<Protected><ProfilePage /></Protected>} />
                    <Route path="/notifications" element={<Protected><NotificationsPage /></Protected>} />
                    <Route path="/settings/preferences" element={<Protected><PreferencesPage /></Protected>} />
                    <Route path="/appearance" element={<Protected><AppearancePage /></Protected>} />

                    {/* 404 */}
                    <Route path="*" element={<Suspense fallback={<PageLoader />}><NotFoundPage /></Suspense>} />
                    </Routes>
                    </Suspense>
                  </LoadingProvider>
                  </SystemNotificationsProvider>
                </NotificationProvider>
              </AgentProvider>
            </OrganizationProvider>
          </AuthProvider>
        </ThemeProvider>
        </BrandingProvider>
      </ErrorProvider>
    </ErrorBoundary>
  );
}

export default App;
